import React, { useState, useMemo, useEffect } from "react";
import { approvalPlugins } from "../../../approvals/registry";
import { APPROVES_PAGE_PATH } from "../../../config/approvalAccess";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiChevronLeft,
} from "react-icons/fi";
import { buildPathToNavModuleMap } from "../../../_nav";
import transferStyles from "./PageAccessTransfer.module.scss";

const PANEL_PAGE_SIZE = 8;
const PANEL_PAGINATION_OPTIONS = {
  skipPrevious: true,
  previous: true,
  next: true,
  skipNext: true,
  selectQuantity: true,
  simpleCounting: false,
};

export const PAGE_GROUPS = [
  {
    label: "Ürün Yönetimi",
    pages: [
      { path: "/device-models", name: "Ürün Modelleri" },
      { path: "/products", name: "Ürünler" },
      { path: "/assembly-products", name: "Montaj" },
      { path: "/packaging-products", name: "Paketleme" },
      { path: "/stock-products", name: "Stok Ürünleri" },
      { path: "/defective-products", name: "Arızalı Ürünler" },
      { path: "/serial-numbers", name: "Seri Numaraları" },
    ],
  },
  {
    label: "Sipariş Yönetimi",
    pages: [
      { path: "/companies", name: "Cariler" },
      { path: "/dealers", name: "Bayiler" },
      { path: "/orders", name: "Teklifler" },
      { path: "/permission-approves", name: "Onaylar" },
      { path: "/device-sales", name: "Siparişler" },
      { path: "/deliveries", name: "Sevkiyatlar" },
    ],
  },
  {
    label: "Üretim — İzleme & Raporlar",
    pages: [
      { path: "/epc", name: "EPC" },
      { path: "/machine-monitor", name: "Makine Dashboard" },
      { path: "/device-monitor", name: "Üretim İzleme" },
      { path: "/activities", name: "Aktiviteler" },
      { path: "/teep", name: "TEEP Şelalesi" },
      { path: "/reports/machine-report", name: "Makine Raporu" },
      { path: "/reports/oee", name: "Özet Rapor" },
      { path: "/reports/oee/by-machine", name: "Makine Bazlı Rapor" },
      { path: "/reports/production", name: "Üretim Raporları" },
      { path: "/reports/production/by-part/per-part", name: "Üretim Özeti" },
      {
        path: "/reports/production/by-part/trend",
        name: "Üretim Çizgi Grafiği",
      },
      { path: "/reports/production/by-part/matrix", name: "Üretim Matrisi" },
      {
        path: "/reports/production/by-machine/trend",
        name: "Üretim Çizgi Grafiği",
      },
      {
        path: "/reports/production/by-machine/distribution",
        name: "Günlük Üretim Tablosu",
      },
      { path: "/reports/production/by-machine/totals", name: "Üretim Özeti" },
      { path: "/reports/quality", name: "Kalite Analizi" },
      { path: "/reports/quality/trend", name: "Özet Raporu" },
      { path: "/reports/quality/by-part", name: "Parça Başına Red" },
      { path: "/reports/downtime", name: "Duruş Analizi" },
      { path: "/reports/downtime/trend", name: "Özet Raporu" },
      { path: "/reports/downtime/reasons", name: "Duruş Sebep Raporu" },
      { path: "/reports/downtime/by-machine", name: "Makine Duruş Raporu" },
      { path: "/reports/device-status", name: "Ürün Analizi" },
      { path: "/reports/activities", name: "Operatör Aktiviteleri" },
      { path: "/production-capacity", name: "Üretim Kapasitesi" },
    ],
  },
  {
    label: "Üretim — Makineler & Envanter",
    pages: [
      { path: "/devices", name: "Cihazlar" },
      { path: "/machines", name: "Makineler" },
      { path: "/machine-assignments", name: "Makine Atamaları" },
      { path: "/parts", name: "Parçalar" },
      { path: "/part-variants", name: "Parça Varyantları" },
      { path: "/part-units", name: "Parça Birimleri" },
      { path: "/part-machines", name: "Parça / Makine" },
      { path: "/part-assemblies", name: "Parça / Montaj" },
      { path: "/inventory", name: "Envanter Listesi" },
      { path: "/inventory-tree", name: "Ürün Ağacı" },
      { path: "/inventory/add", name: "Envanter Ekle" },
      { path: "/inventory/additions", name: "Envanter Girişleri" },
    ],
  },
  {
    label: "İş Emri Modülü & Üretim Planlama",
    pages: [
      { path: "/work-orders", name: "İş Emirleri" },
      { path: "/production-planning", name: "Üretim Planları" },
      { path: "/production-planning/shortage", name: "Eksik Malzeme Raporu" },
    ],
  },
  {
    label: "Üretim — Duruş & OEE",
    pages: [
      { path: "/shifts", name: "Vardiyalar" },
      { path: "/stopping-groups", name: "Duruş Grupları" },
      { path: "/stop-reasons", name: "Duruş Nedenleri" },
      { path: "/machine-stop-logs", name: "Duruş Kayıtları" },
      { path: "/oee-groups", name: "OEE Grupları" },
      { path: "/oee-classes", name: "OEE Sınıfları" },
    ],
  },
  {
    label: "Üretim — Hurda",
    pages: [
      { path: "/scrap-entry", name: "Hurda Girişi" },
      { path: "/scrap-groups", name: "Hurda Grupları" },
    ],
  },
  {
    label: "Raporlar — Sipariş & Teslimat",
    pages: [
      { path: "/reports/sales/trend", name: "Sipariş Trendi" },
      { path: "/reports/sales/by-model", name: "Model Bazlı Sipariş" },
      { path: "/reports/sales/revenue", name: "Gelir Analizi" },
      { path: "/reports/deliveries/trend", name: "Aylık Teslimat" },
      { path: "/reports/deliveries/by-model", name: "Model Bazlı Teslimat" },
    ],
  },
  {
    label: "Kullanıcı Yönetimi",
    pages: [
      { path: "/users", name: "Kullanıcılar" },
      { path: "/user-profiles", name: "Kullanıcı Profilleri" },
      { path: "/approval-workflows", name: "Onay Akışları" },
      { path: "/system/companies", name: "Sistem Firmaları" },
      { path: "/logs", name: "Uygulama Logları" },
      { path: "/settings/notifications", name: "Bildirim Yönetimi" },
      { path: "/account/bank-accounts", name: "Banka Hesapları" },
      { path: "/account/company-settings", name: "Firma Ayarları" },
    ],
  },
];

export const hasPageRead = (pages, path) =>
  (pages || []).some((token) => token === path || token === `${path}:write`);

export const hasPageWrite = (pages, path) =>
  (pages || []).includes(`${path}:write`);

export const getReadPaths = (pages) => {
  const paths = new Set();
  (pages || []).forEach((token) => paths.add(token.split(":")[0]));
  return Array.from(paths);
};

const addPageReadOnly = (pages, path) => {
  const next = new Set(pages || []);
  next.add(path);
  return Array.from(next);
};

const addPageWrite = (pages, path) => {
  const next = new Set(pages || []);
  next.add(path);
  next.add(`${path}:write`);
  return Array.from(next);
};

const removePageAccess = (pages, path) =>
  (pages || []).filter((token) => token.split(":")[0] !== path);

const removePageWriteOnly = (pages, path) =>
  (pages || []).filter((token) => token !== `${path}:write`);

const PATH_NAV_MODULE = buildPathToNavModuleMap();

const ALL_PAGES = PAGE_GROUPS.flatMap((group) =>
  group.pages.map((page) => ({
    ...page,
    groupLabel: group.label,
    navModule: PATH_NAV_MODULE[page.path] || group.label,
  })),
).sort((a, b) => a.name.localeCompare(b.name, "tr"));

const NAV_MODULES = [...new Set(ALL_PAGES.map((p) => p.navModule))].sort(
  (a, b) => a.localeCompare(b, "tr"),
);

const AccessOption = ({ label, checked, onChange, disabled = false }) => (
  <label className={`perm-access-option${disabled ? " is-disabled" : ""}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="perm-access-option-box" />
    <span>{label}</span>
  </label>
);

const PageCollapsibleItem = ({
  page,
  side,
  pages,
  expanded,
  selected,
  pendingGrant,
  onToggleExpand,
  onToggleSelected,
  onSelectAll,
  onClearAll,
  onToggleRead,
  onToggleWrite,
}) => {
  const isLeft = side === "left";
  const hasRead = isLeft
    ? (pendingGrant?.read ?? true)
    : hasPageRead(pages, page.path);
  const hasWrite = isLeft
    ? (pendingGrant?.write ?? true)
    : hasPageWrite(pages, page.path);
  const hasAny = isLeft ? hasRead || hasWrite : hasPageRead(pages, page.path);

  return (
    <div
      className={`perm-page-card${expanded ? " is-open" : ""}${selected ? " is-selected" : ""}${!isLeft && hasAny ? " has-access" : ""}`}
    >
      <div className="perm-page-header">
        <label className="perm-row-select" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelected(page.path)}
          />
          <span className="perm-row-select-box" />
        </label>
        <div
          className="perm-page-header-main"
          role="button"
          tabIndex={0}
          onClick={onToggleExpand}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggleExpand();
            }
          }}
        >
          <span className="perm-page-name">{page.name}</span>
          <span className="perm-page-module">{page.navModule}</span>
        </div>
        <div className="perm-page-header-actions">
          <button
            type="button"
            className="perm-page-action primary"
            onClick={onSelectAll}
          >
            Tümünü Seç
          </button>
          <button
            type="button"
            className="perm-page-action muted"
            onClick={onClearAll}
          >
            Tümünü Kaldır
          </button>
        </div>
        <button
          type="button"
          className="perm-page-expand-btn"
          onClick={onToggleExpand}
          aria-label={expanded ? "Kapat" : "Aç"}
        >
          <FiChevronDown className="perm-page-chevron" />
        </button>
      </div>

      {expanded && (
        <div className="perm-page-body">
          <AccessOption
            label="Görüntüleme"
            checked={hasRead}
            onChange={onToggleRead}
          />
          <AccessOption
            label="Düzenleme"
            checked={hasWrite}
            onChange={onToggleWrite}
            disabled={!hasRead}
          />
        </div>
      )}
    </div>
  );
};

const TransferPanel = ({
  title,
  side,
  pagesList,
  search,
  onSearchChange,
  category,
  onCategoryChange,
  selectedPaths,
  expandedPaths,
  onToggleSelected,
  onSelectAllVisible,
  onClearSelection,
  pages,
  pendingGrants,
  onToggleExpand,
  onItemSelectAll,
  onItemClearAll,
  onToggleRead,
  onToggleWrite,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PANEL_PAGE_SIZE);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return pagesList.filter((pageItem) => {
      if (category !== "all" && pageItem.navModule !== category) return false;
      if (!q) return true;
      return (
        pageItem.name.toLowerCase().includes(q) ||
        pageItem.navModule.toLowerCase().includes(q)
      );
    });
  }, [pagesList, search, category]);

  useEffect(() => {
    setPage(1);
  }, [search, category, pagesList.length]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const paginated = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  const isEmpty = filtered.length === 0;
  const rangeStart = isEmpty ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = isEmpty ? 0 : Math.min(safePage * pageSize, filtered.length);

  return (
    <div className="perm-panel">
      <div className="perm-panel-title">{title}</div>

      <div className="perm-panel-toolbar">
        <div className="perm-search-wrap">
          <FiSearch className="perm-search-icon" />
          <Input
            type="text"
            placeholder="Ara..."
            value={search}
            onChange={onSearchChange}
            className="perm-search-input"
          />
        </div>
        <select
          className="perm-filter-select"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
        >
          <option value="all">Kategori</option>
          {NAV_MODULES.map((mod) => (
            <option key={mod} value={mod}>
              {mod}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="perm-toolbar-btn"
          onClick={onSelectAllVisible}
          disabled={isEmpty}
        >
          Tümünü Seç
        </button>
        <button
          type="button"
          className="perm-toolbar-btn muted"
          onClick={onClearSelection}
          disabled={selectedPaths.size === 0}
        >
          Seçimi Kaldır
        </button>
      </div>

      <div className="perm-panel-body">
        {isEmpty ? (
          <div className="perm-empty">
            {side === "right" ? "Yetki yok" : "Tüm sayfalar atanmış"}
          </div>
        ) : (
          paginated.map((pageItem) => (
            <PageCollapsibleItem
              key={pageItem.path}
              page={pageItem}
              side={side}
              pages={pages}
              expanded={expandedPaths.has(pageItem.path)}
              selected={selectedPaths.has(pageItem.path)}
              pendingGrant={pendingGrants?.[pageItem.path]}
              onToggleExpand={() => onToggleExpand(pageItem.path)}
              onToggleSelected={onToggleSelected}
              onSelectAll={(e) => {
                e.stopPropagation();
                onItemSelectAll(pageItem.path);
              }}
              onClearAll={(e) => {
                e.stopPropagation();
                onItemClearAll(pageItem.path);
              }}
              onToggleRead={(checked) => onToggleRead(pageItem.path, checked)}
              onToggleWrite={(checked) => onToggleWrite(pageItem.path, checked)}
            />
          ))
        )}
      </div>

      {!isEmpty && (
        <div className="perm-panel-pagination">
          <Pagination
            totalItems={filtered.length}
            totalRecords={filtered.length}
            totalPerPage={pageSize}
            currentPage={safePage}
            options={PANEL_PAGINATION_OPTIONS}
            onPageChange={({ pagination: p }) => {
              setPageSize(p.itemsPerPage);
              setPage(p.currentPage);
            }}
          />
        </div>
      )}

      <div className="perm-panel-footer">
        {isEmpty
          ? "Toplam: 0"
          : `Gösterilen: ${rangeStart}–${rangeEnd} / Toplam: ${filtered.length}`}
      </div>
    </div>
  );
};

export const PageAccessSelector = ({
  pages,
  onChange,
  onApprovalAccessChange,
  lockedFullAccess,
}) => {
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [leftCategory, setLeftCategory] = useState("all");
  const [rightCategory, setRightCategory] = useState("all");
  const [leftSelected, setLeftSelected] = useState(new Set());
  const [rightSelected, setRightSelected] = useState(new Set());
  const [leftExpanded, setLeftExpanded] = useState(new Set());
  const [rightExpanded, setRightExpanded] = useState(new Set());
  const [pendingGrants, setPendingGrants] = useState({});

  const unassignedPages = ALL_PAGES.filter((p) => !hasPageRead(pages, p.path));
  const assignedPages = ALL_PAGES.filter((p) => hasPageRead(pages, p.path));

  const getPending = (path) =>
    pendingGrants[path] || { read: true, write: true };

  const setPending = (path, patch) => {
    setPendingGrants((prev) => ({
      ...prev,
      [path]: { ...getPending(path), ...patch },
    }));
  };

  const filterPages = (list, search, category) => {
    const q = search.toLowerCase().trim();
    return list.filter((page) => {
      if (category !== "all" && page.navModule !== category) return false;
      if (!q) return true;
      return (
        page.name.toLowerCase().includes(q) ||
        page.navModule.toLowerCase().includes(q)
      );
    });
  };

  const toggleLeftExpand = (path) => {
    setLeftExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleRightExpand = (path) => {
    setRightExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const transferToAssigned = () => {
    if (leftSelected.size === 0) return;
    let next = pages || [];
    leftSelected.forEach((path) => {
      const grant = getPending(path);
      if (!grant.read) return;
      next = grant.write
        ? addPageWrite(next, path)
        : addPageReadOnly(next, path);
    });
    onChange(next);
    setLeftSelected(new Set());
    setPendingGrants((prev) => {
      const copy = { ...prev };
      leftSelected.forEach((path) => delete copy[path]);
      return copy;
    });
  };

  const transferToUnassigned = () => {
    if (rightSelected.size === 0) return;
    let next = pages || [];
    [...rightSelected].forEach((path) => {
      next = removePageAccess(next, path);
      if (path === APPROVES_PAGE_PATH) {
        onApprovalAccessChange?.([]);
      }
    });
    onChange(next);
    setRightSelected(new Set());
  };

  const toggleRightRead = (path, checked) => {
    if (checked) {
      onChange(addPageReadOnly(pages, path));
    } else {
      onChange(removePageAccess(pages, path));
      if (path === APPROVES_PAGE_PATH) {
        onApprovalAccessChange?.([]);
      }
      setRightSelected((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  };

  const toggleRightWrite = (path, checked) => {
    if (checked) {
      onChange(addPageWrite(pages, path));
    } else {
      onChange(removePageWriteOnly(pages, path));
    }
  };

  if (lockedFullAccess) {
    return (
      <div className="alert alert-secondary mb-0 py-2 small">
        Bu profil tam erişimlidir: tüm sayfalar, yönetim menüleri ve üretim
        hatlarındaki tüm makineler için yetki verilir.
      </div>
    );
  }

  const leftVisible = filterPages(unassignedPages, leftSearch, leftCategory);
  const rightVisible = filterPages(assignedPages, rightSearch, rightCategory);

  return (
    <>
      <PageAccessStyles />
      <div className="perm-transfer-container">
        <TransferPanel
          title="Verilmemiş Yetkiler"
          side="left"
          pagesList={unassignedPages}
          search={leftSearch}
          onSearchChange={setLeftSearch}
          category={leftCategory}
          onCategoryChange={setLeftCategory}
          selectedPaths={leftSelected}
          expandedPaths={leftExpanded}
          onToggleSelected={(path) => {
            setLeftSelected((prev) => {
              const next = new Set(prev);
              if (next.has(path)) next.delete(path);
              else next.add(path);
              return next;
            });
          }}
          onSelectAllVisible={() =>
            setLeftSelected(new Set(leftVisible.map((p) => p.path)))
          }
          onClearSelection={() => setLeftSelected(new Set())}
          pages={pages}
          pendingGrants={pendingGrants}
          onToggleExpand={toggleLeftExpand}
          onItemSelectAll={(path) =>
            setPending(path, { read: true, write: true })
          }
          onItemClearAll={(path) =>
            setPending(path, { read: false, write: false })
          }
          onToggleRead={(path, checked) => {
            setPending(path, {
              read: checked,
              write: checked ? getPending(path).write : false,
            });
          }}
          onToggleWrite={(path, checked) => {
            const pending = getPending(path);
            setPending(path, {
              read: checked ? true : pending.read,
              write: checked,
            });
          }}
        />

        <div className={transferStyles.transferActions}>
          <Button
            appearance="primary-subtle"
            size="small"
            isBlock
            disabled={leftSelected.size === 0}
            onClick={transferToAssigned}
            iconBefore={<FiChevronRight size={16} />}
          />
          <Button
            appearance="neutral"
            size="small"
            isBlock
            disabled={rightSelected.size === 0}
            onClick={transferToUnassigned}
            iconBefore={<FiChevronLeft size={16} />}
          />
        </div>

        <TransferPanel
          title="Verilmiş Yetkiler"
          side="right"
          pagesList={assignedPages}
          search={rightSearch}
          onSearchChange={setRightSearch}
          category={rightCategory}
          onCategoryChange={setRightCategory}
          selectedPaths={rightSelected}
          expandedPaths={rightExpanded}
          onToggleSelected={(path) => {
            setRightSelected((prev) => {
              const next = new Set(prev);
              if (next.has(path)) next.delete(path);
              else next.add(path);
              return next;
            });
          }}
          onSelectAllVisible={() =>
            setRightSelected(new Set(rightVisible.map((p) => p.path)))
          }
          onClearSelection={() => setRightSelected(new Set())}
          pages={pages}
          onToggleExpand={toggleRightExpand}
          onItemSelectAll={(path) => onChange(addPageWrite(pages, path))}
          onItemClearAll={(path) => {
            onChange(removePageAccess(pages, path));
            if (path === APPROVES_PAGE_PATH) {
              onApprovalAccessChange?.([]);
            }
          }}
          onToggleRead={toggleRightRead}
          onToggleWrite={toggleRightWrite}
        />
      </div>
    </>
  );
};

export const PageReadAccessSelector = PageAccessSelector;
export const PageWriteAccessSelector = PageAccessSelector;

export const ApprovalAccessSelector = ({
  approvalAccess,
  onApprovalAccessChange,
}) => {
  const toggleApprovalEntity = (entityType, checked) => {
    const current = approvalAccess || [];
    if (checked) {
      const next = new Set(current);
      next.add(entityType);
      next.add(`${entityType}:write`);
      onApprovalAccessChange(Array.from(next));
    } else {
      onApprovalAccessChange(
        current.filter((t) => t !== entityType && t !== `${entityType}:write`),
      );
    }
  };

  return (
    <div className="mt-2">
      <div className="page-access-header">
        <span className="page-access-header-label">Onay Türü Erişimi</span>
      </div>
      <p className="small text-muted mb-2 px-1">
        Onaylar sayfası yetkisi için en az bir tür işaretlenmelidir.
      </p>
      <div className="page-access-wrapper p-2 bg-white border rounded shadow-sm">
        {Object.entries(approvalPlugins).map(([entityType, plugin]) => {
          const checked = (approvalAccess || []).includes(
            `${entityType}:write`,
          );
          return (
            <label
              key={entityType}
              className="page-access-check d-flex align-items-center gap-2 py-1 pl-1 pr-1"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) =>
                  toggleApprovalEntity(entityType, e.target.checked)
                }
              />
              <span className="page-access-check-box" />
              <span>{plugin.typeLabel || entityType}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const PageAccessStyles = () => (
  <style>{`
    .perm-transfer-container {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 16px;
      align-items: stretch;
    }
    .perm-panel {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #fff;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
    }
    .perm-panel-title {
      padding: 14px 18px;
      font-size: 15px;
      font-weight: 700;
      color: #1e293b;
      border-bottom: 1px solid #e2e8f0;
    }
    .perm-panel-toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 14px;
      border-bottom: 1px solid #e2e8f0;
      background: #f8fafc;
      align-items: center;
    }
    .perm-search-wrap {
      position: relative;
      flex: 1;
      min-width: 160px;
    }
    .perm-search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
      width: 16px;
      height: 16px;
      pointer-events: none;
      z-index: 1;
    }
    .perm-search-input input {
      padding-left: 32px !important;
      font-size: 13px !important;
    }
    .perm-filter-select {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      color: #475569;
      background: #fff;
      min-width: 120px;
    }
    .perm-toolbar-btn {
      border: none;
      background: transparent;
      color: var(--m-primary, #0ea5e9);
      font-size: 13px;
      font-weight: 600;
      padding: 6px 8px;
      cursor: pointer;
      white-space: nowrap;
    }
    .perm-toolbar-btn.muted { color: #64748b; }
    .perm-toolbar-btn:hover:not(:disabled) { text-decoration: underline; }
    .perm-toolbar-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .perm-panel-body {
      flex: 1 1 auto;
      min-height: 0;
      max-height: 520px;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px 14px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      background: #fafbfc;
    }
    .perm-panel-pagination {
      flex-shrink: 0;
      padding: 6px 10px;
      border-top: 1px solid #e2e8f0;
      background: #fff;
    }
    .perm-page-card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      background: #fff;
      overflow: hidden;
      flex-shrink: 0;
      transition: box-shadow 0.15s ease;
    }
    .perm-page-card.has-access {
      border-color: #bfdbfe;
    }
    .perm-page-card.is-open {
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
    }
    .perm-page-card.is-selected {
      border-color: #93c5fd;
      box-shadow: 0 0 0 1px #bfdbfe;
    }
    .perm-page-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      background: #f1f5f9;
      user-select: none;
      flex-shrink: 0;
      min-height: 52px;
    }
    .perm-page-card.is-open .perm-page-header {
      background: #eef6ff;
      border-bottom: 1px solid #e2e8f0;
    }
    .perm-page-header-main {
      flex: 1;
      min-width: 0;
      cursor: pointer;
    }
    .perm-row-select {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
      position: relative;
      margin: 0;
    }
    .perm-row-select input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }
    .perm-row-select-box {
      width: 18px;
      height: 18px;
      border: 2px solid #cbd5e1;
      border-radius: 4px;
      background: #fff;
      position: relative;
      transition: all 0.15s ease;
    }
    .perm-row-select input:checked + .perm-row-select-box {
      background: var(--m-primary, #0ea5e9);
      border-color: var(--m-primary, #0ea5e9);
    }
    .perm-row-select input:checked + .perm-row-select-box::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 50%;
      width: 4px;
      height: 8px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: translate(-50%, -60%) rotate(45deg);
    }
    .perm-page-expand-btn {
      border: none;
      background: none;
      padding: 0;
      display: flex;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
    }
    .perm-page-name {
      display: block;
      font-size: 14px;
      font-weight: 700;
      color: var(--m-primary, #0284c7);
      line-height: 1.3;
    }
    .perm-page-module {
      display: block;
      font-size: 12px;
      color: #64748b;
      margin-top: 2px;
    }
    .perm-page-header-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }
    .perm-page-action {
      border: none;
      background: none;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
      white-space: nowrap;
    }
    .perm-page-action.primary {
      color: var(--m-primary, #0284c7);
    }
    .perm-page-action.muted {
      color: #94a3b8;
    }
    .perm-page-action:hover:not(:disabled) {
      text-decoration: underline;
    }
    .perm-page-action:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .perm-page-chevron {
      width: 18px;
      height: 18px;
      color: #64748b;
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }
    .perm-page-card.is-open .perm-page-chevron {
      transform: rotate(180deg);
    }
    .perm-page-body {
      padding: 14px 16px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      background: #fff;
    }
    .perm-access-option {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 500;
      color: #334155;
      cursor: pointer;
      user-select: none;
      position: relative;
    }
    .perm-access-option.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .perm-access-option input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }
    .perm-access-option-box {
      width: 18px;
      height: 18px;
      border: 2px solid #cbd5e1;
      border-radius: 4px;
      background: #fff;
      position: relative;
      flex-shrink: 0;
      transition: all 0.15s ease;
    }
    .perm-access-option input:checked + .perm-access-option-box {
      background: var(--m-primary, #0ea5e9);
      border-color: var(--m-primary, #0ea5e9);
    }
    .perm-access-option input:checked + .perm-access-option-box::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 50%;
      width: 4px;
      height: 8px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: translate(-50%, -60%) rotate(45deg);
    }
    .perm-panel-footer {
      padding: 10px 16px;
      border-top: 1px solid #e2e8f0;
      font-size: 13px;
      color: #64748b;
      background: #f8fafc;
    }
    .perm-empty {
      padding: 48px 16px;
      text-align: center;
      color: #94a3b8;
      font-size: 14px;
    }
    .page-access-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 0 12px; margin-bottom: 4px; }
    .page-access-header-label { font-size: 14px; font-weight: 600; color: #212529; }
    .page-access-wrapper { border: 1px solid #e2e5e8; border-radius: 8px; overflow: hidden; background: #fff; }
    .page-access-check {
      display: inline-flex; align-items: center; gap: 8px;
      font-size: 12px; font-weight: 500; color: #495057; cursor: pointer; user-select: none;
    }
    .page-access-check input { position: absolute; opacity: 0; width: 0; height: 0; }
    .page-access-check-box {
      width: 16px; height: 16px; flex-shrink: 0;
      border: 2px solid #ced4da; border-radius: 4px; background: #fff;
      transition: all 0.2s ease; position: relative;
    }
    .page-access-check input:checked + .page-access-check-box {
      background: var(--m-primary); border-color: var(--m-primary);
    }
    .page-access-check input:checked + .page-access-check-box::after {
      content: ""; position: absolute; left: 50%; top: 50%;
      width: 4px; height: 8px; border: solid #fff; border-width: 0 2px 2px 0;
      transform: translate(-50%, -60%) rotate(45deg);
    }
    @media (max-width: 1100px) {
      .perm-transfer-container {
        grid-template-columns: 1fr;
      }
      .perm-panel-body {
        max-height: 420px;
      }
    }
    @media (max-width: 768px) {
      .perm-page-header {
        flex-wrap: wrap;
      }
      .perm-page-header-actions {
        order: 3;
        width: 100%;
        padding-top: 4px;
      }
    }
  `}</style>
);
